import { diasUteis, parseNumero } from "./calculos";
import { isNo, normalizeText, toDisplayText } from "./data";
import { F } from "./gerencialFields";
import {
  getDiasBloqueio,
  getLeadTimeMarcenariaProducao,
  getMarcenariaDeadline,
} from "./marcenaria";
import { compareDatesAsc, isBeforeToday, startOfDay, toDate } from "./status";
import { getTipoAmbienteSeguro } from "./tipoAmbiente";

const EMPTY_VALUE = "\u2014";
// I-080: pesos do score expostos para a UI exibir composicao via tooltip.
export const SCORE_WEIGHTS = {
  prazo: 30,
  produtividade: 25,
  disciplina: 15,
};

export const SCORE_DESCRICAO = {
  prazo: "OTD - On Time Delivery (% de ambientes entregues no prazo)",
  produtividade: "Esforco produtivo / dias produtivos (vs benchmark mediano da equipe)",
  disciplina: "% de apontamentos com data de início + termino + prazo preenchidos",
};

function round(value, decimals = 1) {
  if (!Number.isFinite(value)) {
    return null;
  }

  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function average(values, decimals = 1) {
  const validValues = values.filter((value) => Number.isFinite(value));

  if (!validValues.length) {
    return null;
  }

  return round(
    validValues.reduce((accumulator, value) => accumulator + value, 0) / validValues.length,
    decimals
  );
}

function percent(part, total, decimals = 1) {
  if (!total) {
    return null;
  }

  return round((part / total) * 100, decimals);
}

function sum(values) {
  return values.reduce((accumulator, value) => accumulator + (Number.isFinite(value) ? value : 0), 0);
}

function getNumericField(row, field) {
  return parseNumero(row[field]) || 0;
}

function getRouteId(value) {
  return (
    normalizeText(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "marceneiro"
  );
}

function getWeekKey(value) {
  const date = startOfDay(value);

  if (!date) {
    return null;
  }

  const day = date.getDay();
  const distanceFromMonday = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - distanceFromMonday);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const dayOfMonth = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${dayOfMonth}`;
}

function getLatestValidDate(values, limitDate = null) {
  const validDates = values
    .map(toDate)
    .filter(Boolean)
    .filter((date) => !limitDate || date <= limitDate)
    .sort((left, right) => left.getTime() - right.getTime());

  return validDates.length ? validDates[validDates.length - 1] : null;
}

function getPreviousStageDate(row) {
  const startDate = toDate(row[F.MARCENARIA_INICIO]);
  const previous = getLatestValidDate(
    [
      row[F.FINAL_MAQUINAS],
      row[F.DATA_DE_USINAGEM],
      row[F.DATA_DE_FURACAO],
      row[F.DATA_DE_BORDO],
      row[F.DATA_DE_CORTE],
    ],
    startDate
  );

  if (previous) {
    return previous;
  }

  return getLatestValidDate([
    row[F.FINAL_MAQUINAS],
    row[F.DATA_DE_USINAGEM],
    row[F.DATA_DE_FURACAO],
    row[F.DATA_DE_BORDO],
    row[F.DATA_DE_CORTE],
  ]);
}

function getBusinessDaysInclusive(start, end) {
  const startDate = toDate(start);
  const endDate = toDate(end);

  if (!startDate || !endDate || compareDatesAsc(startDate, endDate) > 0) {
    return null;
  }

  const days = diasUteis(startDate, endDate);
  return Number.isFinite(days) && days >= 0 ? days : null;
}

function getQueueDays(row) {
  const startDate = toDate(row[F.MARCENARIA_INICIO]);
  const previousDate = getPreviousStageDate(row);

  if (!startDate || !previousDate || compareDatesAsc(previousDate, startDate) > 0) {
    return null;
  }

  const days = getBusinessDaysInclusive(previousDate, startDate);
  return days == null ? null : Math.max(0, days - 1);
}

function getLeadTimeDays(row) {
  return getLeadTimeMarcenariaProducao(row);
}

function hasDeadline(row) {
  return Boolean(toDate(getMarcenariaDeadline(row)));
}

function hasProductionStarted(row) {
  return Boolean(toDate(row[F.MARCENARIA_INICIO]));
}

function hasProductionEnded(row) {
  return Boolean(toDate(row[F.MARCENARIA_TERMINO]));
}

export function getMarceneiroName(row, fallback = EMPTY_VALUE) {
  return toDisplayText(row[F.MARCENEIRO_RESPONSAVEL], fallback);
}

export function getMarceneiroRouteId(value) {
  return getRouteId(value);
}

export function getEsforcoEquivalente(row) {
  const m2 = getNumericField(row, F.MDF_M2);
  const pecas = getNumericField(row, F.PECAS_UN);
  const usinagem = getNumericField(row, F.USINAGEM_UN);
  const caixaria = getNumericField(row, F.CAIXARIA_UN);
  const tamburato = getNumericField(row, F.TAMBURATO);
  const ripado = getNumericField(row, F.RIPADO);
  const portas = getNumericField(row, F.PORTAS_PUXADORES);
  const complexidadeTotal = getNumericField(row, F.COMPLEXIDADE_TOTAL);
  const base =
    m2 +
    pecas * 0.12 +
    usinagem * 0.25 +
    caixaria * 0.35 +
    tamburato * 0.5 +
    ripado * 0.5 +
    portas * 0.35;

  if (base <= 0 && complexidadeTotal <= 0) {
    return 0;
  }

  const effortBase = base > 0 ? base : complexidadeTotal;
  const complexityFactor = 1 + Math.min(Math.max(complexidadeTotal, 0), 10) * 0.08;

  return round(effortBase * complexityFactor, 2);
}

function buildRowMetric(row) {
  const leadTime = getLeadTimeDays(row);
  const queueDays = getQueueDays(row);
  const blockDays = getDiasBloqueio(row, {
    periodStart: row[F.MARCENARIA_INICIO],
    periodEnd: row[F.MARCENARIA_TERMINO] || new Date(),
  });
  const effort = getEsforcoEquivalente(row);
  const value = parseNumero(row[F.VALOR_DO_AMBIENTE]) || 0;
  const deadline = getMarcenariaDeadline(row);
  const completed = hasProductionEnded(row);
  const started = hasProductionStarted(row);
  const deadlineDate = toDate(deadline);
  const endDate = toDate(row[F.MARCENARIA_TERMINO]);
  const onTime = Boolean(completed && deadlineDate && endDate && endDate <= deadlineDate);
  const lateOpen = Boolean(!completed && deadline && isBeforeToday(deadline));
  const startWeek = getWeekKey(row[F.MARCENARIA_INICIO]);
  const endWeek = getWeekKey(row[F.MARCENARIA_TERMINO]);

  return {
    row,
    id: `${row[F.NOME_DO_CLIENTE] || "cliente"}-${row[F.AMBIENTE] || "ambiente"}-${row[F.NUMERO_DO_LOTE] || ""}`,
    cliente: toDisplayText(row[F.NOME_DO_CLIENTE], EMPTY_VALUE),
    ambiente: toDisplayText(row[F.AMBIENTE], EMPTY_VALUE),
    tipo: getTipoAmbienteSeguro(row),
    lote: toDisplayText(row[F.NUMERO_DO_LOTE], EMPTY_VALUE),
    prazo: deadline,
    inicio: row[F.MARCENARIA_INICIO],
    termino: row[F.MARCENARIA_TERMINO],
    inicioBloqueio: row[F.INICIO_DO_BLOQUEIO],
    finalBloqueio: row[F.FINAL_DO_BLOQUEIO],
    leadTime,
    blockDays,
    queueDays,
    effort,
    value,
    completed,
    started,
    onTime,
    hasDeadline: hasDeadline(row),
    lateOpen,
    contextSwitch: Boolean(started && (!endWeek || startWeek !== endWeek)),
    dataComplete: Boolean(started && deadlineDate && (!completed || endDate)),
  };
}

function buildTypeRows(metrics) {
  const grouped = new Map();

  metrics.forEach((metric) => {
    if (!grouped.has(metric.tipo)) {
      grouped.set(metric.tipo, {
        tipo: metric.tipo,
        metrics: [],
      });
    }

    grouped.get(metric.tipo).metrics.push(metric);
  });

  return [...grouped.values()]
    .map((group) => {
      const completed = group.metrics.filter((metric) => metric.completed);
      const completedWithDeadline = completed.filter((metric) => metric.hasDeadline);
      const onTime = completedWithDeadline.filter((metric) => metric.onTime).length;
      const leadTimes = completed.map((metric) => metric.leadTime).filter((value) => value != null);
      const productiveDays = sum(leadTimes);
      const effort = sum(group.metrics.map((metric) => metric.effort));
      const completedEffort = sum(completed.map((metric) => metric.effort));

      return {
        tipo: group.tipo,
        qtd: group.metrics.length,
        concluidos: completed.length,
        emAberto: group.metrics.length - completed.length,
        leadTimeMedio: average(leadTimes),
        filaMedia: average(group.metrics.map((metric) => metric.queueDays).filter((value) => value != null)),
        otd: percent(onTime, completedWithDeadline.length),
        valor: sum(group.metrics.map((metric) => metric.value)),
        esforco: round(effort, 1),
        produtividade: productiveDays > 0 ? round(completedEffort / productiveDays, 2) : null,
      };
    })
    .sort((left, right) => right.qtd - left.qtd || right.valor - left.valor);
}

function classifyAbc(sortedRows, valueKey, total) {
  let accumulated = 0;

  return sortedRows.map((row) => {
    const share = total ? (row[valueKey] / total) * 100 : 0;
    accumulated += share;
    const classe = accumulated <= 80 ? "A" : accumulated <= 95 ? "B" : "C";

    return {
      ...row,
      [`${valueKey}Share`]: round(share, 1),
      [`${valueKey}Accumulated`]: round(accumulated, 1),
      [`${valueKey}Class`]: classe,
    };
  });
}

function buildAbcEffortRevenue(typeRows) {
  const totalEffort = sum(typeRows.map((row) => row.esforco));
  const totalRevenue = sum(typeRows.map((row) => row.valor));
  const byEffort = classifyAbc(
    [...typeRows].sort((left, right) => right.esforco - left.esforco),
    "esforco",
    totalEffort
  );
  const byRevenue = classifyAbc(
    [...typeRows].sort((left, right) => right.valor - left.valor),
    "valor",
    totalRevenue
  );
  const revenueByType = new Map(
    byRevenue.map((row) => [
      row.tipo,
      {
        valorShare: row.valorShare,
        valorAccumulated: row.valorAccumulated,
        valorClass: row.valorClass,
      },
    ])
  );

  return byEffort.map((row) => ({
    ...row,
    ...(revenueByType.get(row.tipo) || {
      valorShare: 0,
      valorAccumulated: 0,
      valorClass: "C",
    }),
  }));
}

function buildScore(summary, productivityBenchmark) {
  const otdScore = summary.otd != null ? (summary.otd / 100) * SCORE_WEIGHTS.prazo : 0;
  const productivityRatio =
    summary.produtividade != null && productivityBenchmark > 0
      ? Math.min(summary.produtividade / productivityBenchmark, 1.25)
      : 0;
  const productivityScore = Math.min(1, productivityRatio) * SCORE_WEIGHTS.produtividade;
  const disciplineScore =
    summary.disciplina != null ? (summary.disciplina / 100) * SCORE_WEIGHTS.disciplina : 0;
  const availablePoints =
    SCORE_WEIGHTS.prazo + SCORE_WEIGHTS.produtividade + SCORE_WEIGHTS.disciplina;

  return round(((otdScore + productivityScore + disciplineScore) / availablePoints) * 100, 0);
}

function summarizeGroup(name, metrics, productivityBenchmark = null) {
  const completed = metrics.filter((metric) => metric.completed);
  const completedWithDeadline = completed.filter((metric) => metric.hasDeadline);
  const onTime = completedWithDeadline.filter((metric) => metric.onTime).length;
  const leadTimes = completed.map((metric) => metric.leadTime).filter((value) => value != null);
  const productiveDays = sum(leadTimes);
  const completedEffort = sum(completed.map((metric) => metric.effort));
  const typeRows = buildTypeRows(metrics);
  const started = metrics.filter((metric) => metric.started);
  const dataRows = metrics.filter((metric) => metric.started || metric.completed || metric.hasDeadline);
  const dataComplete = dataRows.filter((metric) => metric.dataComplete).length;
  const productivity = productiveDays > 0 ? round(completedEffort / productiveDays, 2) : null;
  const summary = {
    id: getRouteId(name),
    nome: name,
    metrics,
    ambientes: metrics.length,
    concluidos: completed.length,
    emAndamento: metrics.filter((metric) => metric.started && !metric.completed).length,
    aguardandoInicio: metrics.filter((metric) => !metric.started).length,
    atrasadosAbertos: metrics.filter((metric) => metric.lateOpen).length,
    leadTimeMedio: average(leadTimes),
    filaMedia: average(metrics.map((metric) => metric.queueDays).filter((value) => value != null)),
    otd: percent(onTime, completedWithDeadline.length),
    otdBase: completedWithDeadline.length,
    produtividade: productivity,
    esforco: round(sum(metrics.map((metric) => metric.effort)), 1),
    esforcoConcluido: round(completedEffort, 1),
    diasProdutivos: productiveDays,
    valor: sum(metrics.map((metric) => metric.value)),
    receitaPorDia:
      productiveDays > 0 ? round(sum(completed.map((metric) => metric.value)) / productiveDays, 2) : null,
    trocaContexto: percent(
      started.filter((metric) => metric.contextSwitch).length,
      started.length
    ),
    trocaContextoQtd: started.filter((metric) => metric.contextSwitch).length,
    disciplina: percent(dataComplete, dataRows.length),
    disciplinaBase: dataRows.length,
    tipoRows: typeRows,
    abcRows: buildAbcEffortRevenue(typeRows),
  };

  return {
    ...summary,
    scoreMonitoravel:
      productivityBenchmark == null ? null : buildScore(summary, productivityBenchmark),
  };
}

export function buildMarceneiros(dados) {
  const grouped = new Map();
  const unassigned = [];

  dados
    .filter((row) => isNo(row[F.OBRA_FUTURA]))
    .forEach((row) => {
      const name = getMarceneiroName(row, "");

      if (!name) {
        unassigned.push(row);
        return;
      }

      const key = normalizeText(name);

      if (!grouped.has(key)) {
        grouped.set(key, {
          nome: name,
          rows: [],
        });
      }

      grouped.get(key).rows.push(row);
    });

  const preliminary = [...grouped.values()].map((group) =>
    summarizeGroup(group.nome, group.rows.map(buildRowMetric))
  );
  const productivityValues = preliminary
    .map((summary) => summary.produtividade)
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((left, right) => left - right);
  const middle = Math.floor(productivityValues.length / 2);
  const productivityBenchmark = productivityValues.length
    ? productivityValues.length % 2
      ? productivityValues[middle]
      : (productivityValues[middle - 1] + productivityValues[middle]) / 2
    : 0;

  const marceneiros = [...grouped.values()]
    .map((group) => summarizeGroup(group.nome, group.rows.map(buildRowMetric), productivityBenchmark))
    .sort((left, right) => left.nome.localeCompare(right.nome, "pt-BR", { sensitivity: "base" }));

  const allMetrics = marceneiros.flatMap((marceneiro) => marceneiro.metrics);
  const global = summarizeGroup("Todos os marceneiros", allMetrics, productivityBenchmark);
  const highRevenueThreshold = average(
    allMetrics.map((metric) => metric.value).filter((value) => Number.isFinite(value) && value > 0),
    2
  );
  const highRevenueDelayed = allMetrics
    .filter((metric) => metric.value >= (highRevenueThreshold || 0) && metric.lateOpen)
    .sort((left, right) => right.value - left.value)
    .slice(0, 10);

  return {
    marceneiros,
    global,
    unassignedCount: unassigned.length,
    productivityBenchmark: round(productivityBenchmark, 2),
    highRevenueThreshold,
    highRevenueDelayed,
  };
}
